import { expressionsJson, fontConfigJson, functionsJson, operatorsJson } from "@corca/data";

import type { SymbolFont, SymbolFontContext } from "@corca/data";

const { metrics, symbols } = fontConfigJson;

const NO_FONT: SymbolFont = {};

let keyBySymbol: Record<string, string> | undefined;

type GlyphSource = {
  type?: string;
  props?: { key?: string; value?: string };
  symbol?: string;
  symbolProps?: { key?: string };
};

/** Creates and caches a fallback mapping from glyph to key. */
function getKeyBySymbol() {
  if (keyBySymbol) return keyBySymbol;

  const map: Record<string, string> = {};

  const visit = (node: unknown) => {
    if (Array.isArray(node)) return node.forEach(visit);
    if (!node || typeof node !== "object") return;

    const { type, props, symbol, symbolProps } = node as GlyphSource;
    if (type === "symbol" && props?.key && props.value) {
      map[props.value] = props.key;
    }
    if (symbol && symbolProps?.key) {
      map[symbol] = symbolProps.key;
    }

    Object.values(node).forEach(visit);
  };
  visit([expressionsJson, functionsJson, operatorsJson]);

  keyBySymbol = {};
  for (const [glyph, key] of Object.entries(map)) {
    if (key in symbols) keyBySymbol[glyph] = key;
  }
  return keyBySymbol;
}

function resolve(key: string, { mathStyle, size }: SymbolFontContext): SymbolFont {
  const font = metrics[symbols[key]][mathStyle];
  if (!font) return NO_FONT;
  return { fontFeature: font.fontFeature, fontSize: font.fontSize?.[size] };
}

type SymbolRef = {
  key?: string;
  value?: string;
  props?: SymbolFont;
};

/** The font config entry for a symbol: its own key, or the one its glyph recovers. */
function resolveKey({ key, value }: SymbolRef): string | undefined {
  if (key && key in symbols) return key;

  const recoveredKey = key && value ? getKeyBySymbol()[value] : undefined;
  if (recoveredKey) {
    console.warn(
      `"${value}" has key "${key}", which is not in the font config — resolved by glyph as "${recoveredKey}"`,
    );
  }
  return recoveredKey;
}

/**
 * Font styling for a symbol in the given rendering context.
 *
 * Resolves by key, then falls back to the glyph, then to the node's own props.
 */
export function getSymbolFont(ref: SymbolRef, context: SymbolFontContext): SymbolFont {
  const key = resolveKey(ref);
  if (key) return resolve(key, context);

  const { value, props } = ref;
  if (props?.fontSize || props?.fontFeature) {
    console.warn(`"${value ?? ref.key}" is styled by node props; add it to the font config`);
    return props;
  }

  return NO_FONT;
}

/**
 * Whether a symbol is a large operator (∫, ∑, ∏…).
 */
export function isLargeOperator(ref: SymbolRef): boolean {
  const key = resolveKey(ref);
  if (key) return symbols[key] === "n-ary" || symbols[key] === "integral";

  return !!ref.props?.fontSize;
}
